/** SUNTRONIX ONE — SpacesShell layout (Option A · ONE SPACES).
 *
 *  Removes the traditional sidebar.  Fixed top ONE Bar carries brand,
 *  Space Switcher, global search, +Create, Pulse, Chat, Notifications, Profile.
 *  Directly beneath, a thin sticky Context Ribbon shows the current Space's
 *  destinations.  Main content is the only scrolling region.
 *
 *  Roles authorise which Spaces appear.  All existing routes are preserved —
 *  every route is mapped to exactly one Space in `/constants/spaces.js`.
 */
import { EntityBrandLogo } from "@/components/brand/EntityBrandLogo";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { own } from "@/lib/terminology";
import { toast } from "sonner";
import {
  ChevronDown, LogOut, Laptop2, UserCog, Building2, Plus, Focus, Sparkles,
  ArrowLeft,
} from "lucide-react";
import CommandBar from "@/components/app/CommandBar";
import EstablishmentSwitcher from "@/components/app/EstablishmentSwitcher";
import OneCommand from "@/components/app/OneCommand";
import { OneMomentsProvider } from "@/components/app/OneMoments";
import { accentForSpace } from "@/lib/vibe";
import { applyPrefs } from "@/lib/experience";
import NotificationCentre from "@/components/app/NotificationCentre";
import { SidekickProvider, SidekickTrigger } from "@/components/app/OneSidekick";
import HelpMenu from "@/components/app/HelpMenu";
import LanguageRegionPicker from "@/components/app/LanguageRegionPicker";
import { ChatDrawerButton } from "@/components/app/ChatDrawer";
import ApprovalsInboxChip from "@/components/app/ApprovalsInboxChip";
import ContextHelp from "@/components/os/ContextHelp";
import GlobalKycDrawer, { GlobalKycDrawerButton } from "@/components/app/GlobalKycDrawer";
import UniversalCreate, { InstantCreate } from "@/components/app/UniversalCreate";
import { AdminModeProvider, AdminModeToggle, AdminChip } from "@/components/app/AdminMode";
import ContextRibbon from "@/components/app/ContextRibbon";
import AskOneChat from "@/components/app/AskOneChat";
import StickyNotesWidget from "@/components/app/StickyNotesWidget";
import PreviewBanner, { PreviewDeviceFrame } from "@/components/app/PreviewBanner";
import CustomerWelcomeModal from "@/components/app/CustomerWelcomeModal";
import WhatsNewToast from "@/components/app/WhatsNewToast";
import { WorldClockStrip, FxTicker } from "@/components/app/WorldClockAndFx";
import { customerPathAllowed, isCustomerRole } from "@/constants/customerNav";
import { canSee, canAdminUsers } from "@/constants/roleNav";
import { NAV, AUTH } from "@/constants/testIds";
import {
  SPACES_SUNTRONIX, SPACES_CUSTOMER, spaceIdForPath, defaultSpaceForRole,
  subspaceForPath,
} from "@/constants/spaces";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const FOCUS_KEY = "st.spaces.focus";

/* Shared: any top-nav menu closes when the route path changes,
   plus a small handler that closes on select.  Rules enforced:
   • Close on destination select (dismiss BEFORE navigate).
   • Close on outside click / ESC (Radix defaults, wired via
     onOpenChange).
   • Close after route/path changes.
   • Only one instance's own state — nothing persists across pages.
*/
function useNavMenuOpen() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  useEffect(() => { setOpen(false); }, [location.pathname]);
  return [open, setOpen];
}

export default function SpacesShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [masters, setMasters] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState(null);
  const [focus, setFocus] = useState(() => {
    try { return window.localStorage.getItem(FOCUS_KEY) === "1"; } catch { return false; }
  });
  const [kycOpen, setKycOpen] = useState(false);
  const [finVault, setFinVault] = useState(false);
  const [continuity, setContinuity] = useState(false);
  const [secCentre, setSecCentre] = useState(false);

  useEffect(() => {
    if (!user || isCustomerRole(user.role)) return;
    api.get("/finance-vault/access")
      .then(({ data }) => setFinVault(!!(data.corporate || data.personal)))
      .catch(() => setFinVault(false));
    api.get("/continuity/access")
      .then(({ data }) => setContinuity(!!(data.business || data.personal)))
      .catch(() => setContinuity(false));
    api.get("/security-centre/access")
      .then(({ data }) => setSecCentre(!!data.allowed))
      .catch(() => setSecCentre(false));
  }, [user]);

  useEffect(() => {
    if (!user) return;
    api.get("/master-companies").then(({ data }) => {
      setMasters(data.items || []);
      if (user.master_company_id) setSelectedTenantId(user.master_company_id);
    }).catch(() => {});
  }, [user]);

  const isCustomer = user && isCustomerRole(user.role);
  const spaces = useMemo(() => {
    if (!user) return [];
    const all = isCustomer ? SPACES_CUSTOMER : SPACES_SUNTRONIX;
    return all.filter((s) => {
      if (s.onlySuntronix && isCustomer) return false;
      const anyVisible = s.ribbon.some((r) => (isCustomer ? customerPathAllowed(user.role, r.to) : canSee(user.role, r.to)));
      return anyVisible;
    });
  }, [isCustomer, user]);

  const activeSpaceId = spaceIdForPath(location.pathname, isCustomer);
  const activeSpace = spaces.find((s) => s.id === activeSpaceId) || spaces[0];
  const activeSubspace = subspaceForPath(location.pathname, isCustomer);
  const spaceAccent = accentForSpace(activeSpaceId);

  // Apply personal experience preferences (density / theme / animation) once.
  useEffect(() => { applyPrefs(); }, []);

  // Filter ribbon items by role
  const ribbon = useMemo(() => {
    if (!activeSpace) return [];
    return activeSpace.ribbon.filter((r) => {
      if (r.onlySuntronix) return !isCustomer;
      if (r.onlyEmployee) return user.role === "customer_employee";
      if (r.userAdmin) return canAdminUsers(user.role);
      if (r.financeVault) return finVault;
      if (r.continuity) return continuity;
      if (r.relationships) return !isCustomer;
      if (r.securityCentre) return secCentre;
      return isCustomer ? customerPathAllowed(user.role, r.to) : canSee(user.role, r.to);
    });
  }, [activeSpace, isCustomer, user, finVault, continuity, secCentre]);

  const tenantName = useMemo(() => {
    if (!selectedTenantId) return "Suntronix (All Tenants)";
    return masters.find((m) => m.id === selectedTenantId)?.name || "…";
  }, [masters, selectedTenantId]);
  const activeTenant = useMemo(
    () => masters.find((m) => m.id === selectedTenantId) || null, [masters, selectedTenantId]
  );
  const isDemoTenant = !!activeTenant?.is_demo;

  const handleLogout = useCallback(async () => {
    await logout();
    toast.success("Signed out");
    navigate("/login");
  }, [logout, navigate]);

  const toggleFocus = () => {
    setFocus((prev) => {
      const next = !prev;
      try { window.localStorage.setItem(FOCUS_KEY, next ? "1" : "0"); } catch { /* ignore */ }
      return next;
    });
  };

  return (
    <AdminModeProvider user={user}>
    <SidekickProvider>
    <OneMomentsProvider enabled={!isCustomer}>
    <div style={{ "--st-accent-space": spaceAccent.hex }} className="h-[100dvh] overflow-hidden flex flex-col bg-[color:var(--st-bg)] pb-[env(safe-area-inset-bottom)]" data-testid="spaces-shell">
      <PreviewBanner />
      <a href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:px-3 focus:py-2 focus:rounded-md focus:bg-slate-900 focus:text-white focus:text-sm">
        Skip to main content
      </a>

      {isDemoTenant && (
        <div data-testid="demo-tenant-banner"
          className="bg-amber-100 border-b border-amber-300 text-amber-900 text-[11px] font-medium tracking-wide px-6 py-1.5 flex items-center gap-2 shrink-0">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-600 animate-pulse" />
          <span className="uppercase tracking-[0.14em]">Demo Environment · Fictional Data</span>
          <span className="text-amber-800/70 normal-case tracking-normal">— {activeTenant?.name}</span>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/*  ONE BAR — fixed, elegant.                                          */}
      {/*  Left  : brand + Space Switcher (the What/Where).                   */}
      {/*  Centre: CommandBar (the How — search / ask).                       */}
      {/*  Right : signal icons (max 5) + avatar.                             */}
      {/*  Time + FX live inside a single status popover to keep the bar calm.*/}
      {/* ------------------------------------------------------------------ */}
      <header className="h-14 border-b border-[color:var(--st-border)] bg-[color:var(--st-surface)] px-4 md:px-6 flex items-center gap-3 shrink-0" data-testid="one-bar">
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="one-bar-brand">
          <span className="shrink-0"><EntityBrandLogo /></span>
        </Link>

        <div className="hidden md:block h-6 w-px bg-[color:var(--st-border)] mx-1 shrink-0" />

        {!isCustomer && <EstablishmentSwitcher />}
        <span className="hidden md:inline-flex">
          <AdminChip area="modules" label="Edit navigation" testid="admin-chip-nav" className="ml-1" />
        </span>

        <div className="flex-1 min-w-0 mx-2 max-w-xl">
          {isCustomer
            ? <CommandBar testId={NAV.searchInput} />
            : <OneCommand testId={NAV.searchInput} />}
        </div>

        {!isCustomer && (
          <button
            onClick={() => navigate("/one-ai")}
            data-testid="one-bar-one-ai"
            aria-label="Open ONE AI"
            title="ONE AI — enterprise intelligence"
            className="hidden md:inline-flex items-center gap-1.5 shrink-0 h-9 px-3 rounded-full text-[12px] font-semibold text-white transition-transform hover:scale-[1.03]"
            style={{ background: "var(--st-accent)" }}
          >
            <Sparkles className="h-4 w-4" strokeWidth={1.9} />
            ONE AI
          </button>
        )}

        {/* Right-side icon strip — visually calmer, grouped in 4s.
            On mobile only the essentials stay; the rest collapse to md+ so
            the header never overflows a 390px viewport. */}
        <div className="ml-auto flex items-center gap-0.5">
          <span className="hidden md:inline-flex"><AdminModeToggle /></span>
          <span className="hidden md:inline-flex"><GlobalKycDrawerButton onOpen={() => setKycOpen(true)} /></span>
          <ContextHelp />
          <ApprovalsInboxChip />
          <span className="hidden md:inline-flex"><ChatDrawerButton /></span>
          {!isCustomer && <span className="hidden md:inline-flex"><SidekickTrigger /></span>}
          <NotificationsPopover />
          <span className="hidden md:inline-flex"><StatusChip /></span>
          <button onClick={toggleFocus} data-testid="one-bar-focus"
            aria-label={focus ? "Exit focus mode" : "Enter focus mode"}
            title={focus ? "Exit focus mode" : "Enter focus mode"}
            className={
              "hidden md:grid h-9 w-9 place-items-center rounded-md transition-colors " +
              (focus ? "bg-[color:var(--st-text)] text-white" : "text-[color:var(--st-text-muted)] hover:text-[color:var(--st-text)] hover:bg-[color:var(--st-surface-alt)]")
            }>
            <Focus className="h-4 w-4" strokeWidth={1.6} />
          </button>
          <ProfileMenu user={user} onLogout={handleLogout} onSwitchShell={() => {
            try { window.localStorage.removeItem("st.shell"); } catch { /* ignore */ }
            window.location.reload();
          }} />
        </div>
      </header>

      {/* ------------------------------------------------------------------ */}
      {/*  CONTEXT RIBBON — full ribbon on lg+, "View ▾" sheet on < lg.       */}
      {/* ------------------------------------------------------------------ */}
      {!focus && activeSpace && (
        <ContextRibbon
          spaceLabel={(activeSubspace || activeSpace).label}
          ribbon={activeSubspace ? activeSubspace.ribbon : ribbon}
          leadingSlot={activeSubspace ? (
            <div className="flex items-center gap-1.5 shrink-0">
              <button
                type="button"
                data-testid="subspace-back"
                onClick={() => navigate(activeSubspace.parentLanding)}
                aria-label={`Back to ${activeSpace.label}`}
                className="h-8 w-8 grid place-items-center rounded-md text-[color:var(--st-text-muted)] hover:text-[color:var(--st-text)] hover:bg-[color:var(--st-surface-alt)]"
              >
                <ArrowLeft className="h-4 w-4" strokeWidth={1.8} />
              </button>
              <SubspaceSwitcher subspace={activeSubspace} parentLabel={activeSpace.label}
                onBack={() => navigate(activeSubspace.parentLanding)} />
            </div>
          ) : (
            <SpaceSwitcher
              spaces={spaces}
              activeId={activeSpace?.id}
              onSelect={(space) => navigate(space.landing)}
            />
          )}
          actionSlot={<CreateMenu spaceId={activeSpace?.id} isCustomer={isCustomer} />}
        />
      )}

      {/* ------------------------------------------------------------------ */}
      {/*  MAIN CONTENT — the ONLY vertical scroll region.                    */}
      {/* ------------------------------------------------------------------ */}
      <div
        id="main-content"
        tabIndex={-1}
        role="main"
        data-scroll-container="true"
        className="flex-1 min-h-0 overflow-y-auto px-3 md:px-5 lg:px-6 py-3 pb-48 md:pb-32"
      >
        <div className="mx-auto max-w-[1600px]">
          <PreviewDeviceFrame>
            <Outlet />
          </PreviewDeviceFrame>
        </div>
      </div>

      {/* Ask ONE — floating conversational assistant */}
      <AskOneChat />
      <StickyNotesWidget />
      {isCustomer && <CustomerWelcomeModal user={user} />}
      <WhatsNewToast user={user} />
      <GlobalKycDrawer open={kycOpen} onClose={() => setKycOpen(false)} user={user} />

      {/* ------------------------------------------------------------------ */}
      {/*  MOBILE BOTTOM NAV — §104.  Hidden on ≥ md screens.                 */}
      {/*  Five destinations only, respecting safe-area inset.                */}
      {/* ------------------------------------------------------------------ */}
      <MobileBottomNav navigate={navigate} spaces={spaces} activeId={activeSpace?.id} />
    </div>
    </OneMomentsProvider>
    </SidekickProvider>
    </AdminModeProvider>
  );
}

function MobileBottomNav({ navigate, spaces, activeId }) {
  const items = [
    { key: "home",   label: "Home",   to: "/",         testId: "mobile-nav-home" },
    { key: "work",   label: "Work",   to: "/work",     testId: "mobile-nav-work" },
    { key: "search", label: "Search", onClick: () => {
        // Focus the CommandBar search input at the top
        const el = document.querySelector("[data-testid='global-search-input']") || document.querySelector("[data-testid='nav-search-input']");
        if (el) el.focus();
    }, testId: "mobile-nav-search" },
    { key: "create", label: "Create", onClick: () => {
        const btn = document.querySelector("[data-testid='one-bar-create']");
        if (btn) btn.click();
    }, testId: "mobile-nav-create" },
    { key: "more",   label: "More",   onClick: () => {
        const btn = document.querySelector("[data-testid='space-switcher']");
        if (btn) btn.click();
    }, testId: "mobile-nav-more" },
  ];
  return (
    <nav
      aria-label="Mobile navigation"
      data-testid="mobile-bottom-nav"
      className="md:hidden fixed bottom-0 inset-x-0 z-40 h-16 border-t border-[color:var(--st-border)] bg-[color:var(--st-surface)] flex items-stretch justify-around pb-[env(safe-area-inset-bottom)]"
    >
      {items.map((it) => (
        <button
          key={it.key}
          onClick={() => (it.to ? navigate(it.to) : it.onClick?.())}
          data-testid={it.testId}
          className="flex-1 flex flex-col items-center justify-center gap-0.5 text-[10px] uppercase tracking-[0.12em] text-[color:var(--st-text-muted)] hover:text-[color:var(--st-text)] active:bg-[color:var(--st-surface-alt)]"
        >
          <div className={
            "h-1.5 w-6 rounded-full " +
            (activeId === it.key ? "bg-[color:var(--st-accent)]" : "bg-transparent")
          } />
          <span className="text-[11px] font-medium text-[color:var(--st-text)]">{it.label}</span>
        </button>
      ))}
    </nav>
  );
}

/* ------------------------------------------------------------------ */
function SubspaceSwitcher({ subspace, parentLabel, onBack }) {
  const [open, setOpen] = useNavMenuOpen();
  const Icon = subspace.icon;
  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="subspace-switcher"
          aria-expanded={open}
          className="h-9 px-2.5 rounded-md flex items-center gap-2 text-[13px] font-semibold text-[color:var(--st-text)] hover:bg-[color:var(--st-surface-alt)]"
        >
          <Icon className="h-4 w-4 text-[color:var(--st-accent-space,var(--st-accent))]" strokeWidth={1.7} />
          <span>{subspace.label}</span>
          <ChevronDown className="h-3.5 w-3.5 text-[color:var(--st-text-muted)]" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={8}
        className="w-[240px] p-2 border-[color:var(--st-border)]"
        data-testid="subspace-switcher-menu"
        onEscapeKeyDown={() => setOpen(false)}
        onPointerDownOutside={() => setOpen(false)}
        onInteractOutside={() => setOpen(false)}>
        <div className="px-2 pb-1.5 text-[10px] uppercase tracking-[0.16em] text-[color:var(--st-text-subtle)]">Inside {subspace.label}</div>
        <button
          onClick={() => { setOpen(false); onBack(); }}
          data-testid="subspace-back-to-parent"
          className="w-full text-left px-2 py-2 rounded-md flex items-center gap-2.5 text-[13px] text-[color:var(--st-text)] hover:bg-[color:var(--st-surface-alt)]"
        >
          <ArrowLeft className="h-4 w-4 text-[color:var(--st-text-muted)]" strokeWidth={1.7} />
          <span className="flex-1">Back to {parentLabel}</span>
        </button>
      </PopoverContent>
    </Popover>
  );
}

/* ------------------------------------------------------------------ */
function SpaceSwitcher({ spaces, activeId, onSelect }) {
  const current = spaces.find((s) => s.id === activeId) || spaces[0];
  const [open, setOpen] = useNavMenuOpen();

  if (!current) return null;
  const Icon = current.icon;

  const handleSelect = (s) => {
    setOpen(false);           // Rule: dismiss BEFORE navigation.
    onSelect(s);
  };

  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="space-switcher"
          aria-expanded={open}
          className="h-9 px-2.5 rounded-md flex items-center gap-2 text-[13px] font-medium text-[color:var(--st-text)] hover:bg-[color:var(--st-surface-alt)]"
        >
          <Icon className="h-4 w-4 text-[color:var(--st-accent-space,var(--st-accent))]" strokeWidth={1.6} />
          <span>{current.label}</span>
          <ChevronDown className="h-3.5 w-3.5 text-[color:var(--st-text-muted)]" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={8}
        className="w-[280px] p-2 border-[color:var(--st-border)]"
        data-testid="space-switcher-menu"
        onEscapeKeyDown={() => setOpen(false)}
        onPointerDownOutside={() => setOpen(false)}
        onInteractOutside={() => setOpen(false)}>
        <div className="px-2 pb-1.5 text-[10px] uppercase tracking-[0.16em] text-[color:var(--st-text-subtle)]">Switch space</div>
        {spaces.map((s) => {
          const SIcon = s.icon;
          const active = s.id === activeId;
          return (
            <button
              key={s.id}
              onClick={() => handleSelect(s)}
              data-testid={`space-${s.id}`}
              className={
                "w-full text-left px-2 py-2 rounded-md flex items-center gap-2.5 text-[13px] " +
                (active
                  ? "bg-[color:var(--st-text)] text-white"
                  : "text-[color:var(--st-text)] hover:bg-[color:var(--st-surface-alt)]")
              }
            >
              <SIcon className={"h-4 w-4 " + (active ? "text-[color:var(--st-accent)]" : "text-[color:var(--st-text-muted)]")} strokeWidth={1.6} />
              <span className="flex-1">{s.label}</span>
            </button>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}

/* ------------------------------------------------------------------ */
// Read-only tenant roles must never see write entry-points like + Create.
const READ_ONLY_CUSTOMER_ROLES = new Set(["customer_viewer", "customer_auditor"]);

function CreateMenu({ spaceId, isCustomer }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const readOnly = READ_ONLY_CUSTOMER_ROLES.has(user?.role);
  useEffect(() => {
    if (readOnly) return;
    const onKey = (e) => {
      const el = document.activeElement;
      const typing = el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable);
      if (typing || e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === "c" || e.key === "C") { e.preventDefault(); setOpen(true); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [readOnly]);
  if (readOnly) return null;
  return (
    <>
      {!isCustomer && <InstantCreate role={user?.role} compact />}
      <button
        data-testid="one-bar-create"
        onClick={() => setOpen(true)}
        aria-expanded={open}
        className="h-9 px-3 rounded-md bg-[color:var(--st-text)] text-white text-[12px] font-medium hover:bg-[#17263C] flex items-center gap-1.5"
      >
        <Plus className="h-4 w-4" strokeWidth={1.8} />
        <span className="hidden md:inline">Create</span>
      </button>
      <UniversalCreate open={open} onOpenChange={setOpen}
        role={user?.role} isCustomer={isCustomer} />
    </>
  );
}

function PulsePopover() {
  return null;   /* Retired — signals folded into StatusChip + Approvals + Notifications. */
}

export function NotificationsPopover() {
  const [open, setOpen] = useNavMenuOpen();
  const { user } = useAuth();
  const [unread, setUnread] = useState(0);
  const isInternal = user && !isCustomerRole(user.role);
  useEffect(() => {
    if (!isInternal) return;
    const poll = () => api.get("/collab/notifications?unread_only=true")
      .then(({ data }) => setUnread(data.unread || 0)).catch(() => {});
    poll();
    const t = setInterval(poll, 30000);
    return () => clearInterval(t);
  }, [isInternal, open]);
  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverTrigger asChild>
        <button data-testid="one-bar-notifications" aria-label="Notifications"
          aria-expanded={open}
          className="relative h-9 w-9 grid place-items-center rounded-md text-[color:var(--st-text-muted)] hover:text-[color:var(--st-text)] hover:bg-[color:var(--st-surface-alt)] transition-colors">
          <BellIcon />
          {unread > 0 && (
            <span data-testid="notif-unread-badge"
              className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-0.5 rounded-full bg-red-500 text-white text-[9px] font-semibold grid place-items-center">
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={8} className="w-[360px] p-4"
        onEscapeKeyDown={() => setOpen(false)}
        onPointerDownOutside={() => setOpen(false)}
        onInteractOutside={() => setOpen(false)}>
        {isInternal ? (
          <NotificationCentre onNavigate={() => setOpen(false)} />
        ) : (
          <>
            <div className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--st-text-subtle)] mb-2">Notifications</div>
            <div className="text-[12px] text-[color:var(--st-text-muted)]">You’re all caught up.</div>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}


/* ------------------------------------------------------------------ */
/*  StatusChip — one calm chip that folds World Clock + FX + Region +  */
/*  Help behind a single click.  Replaces four separate header icons.  */
/* ------------------------------------------------------------------ */
export function StatusChip() {
  const [open, setOpen] = useNavMenuOpen();
  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverTrigger asChild>
        <button data-testid="one-bar-status"
          aria-label="Region, time and rates"
          aria-expanded={open}
          title="Region, time and rates"
          className="h-9 w-9 grid place-items-center rounded-md text-[color:var(--st-text-muted)] hover:text-[color:var(--st-text)] hover:bg-[color:var(--st-surface-alt)] transition-colors">
          <Globe2Icon />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={8} className="w-[380px] p-0"
        onEscapeKeyDown={() => setOpen(false)}
        onPointerDownOutside={() => setOpen(false)}
        onInteractOutside={() => setOpen(false)}>
        <div className="p-4 border-b border-[color:var(--st-border)]">
          <div className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--st-text-subtle)] mb-2">World clock</div>
          <WorldClockStrip />
          <div className="mt-3 text-[10px] uppercase tracking-[0.16em] text-[color:var(--st-text-subtle)] mb-2">Currency</div>
          <FxTicker />
        </div>
        <div className="p-3 border-b border-[color:var(--st-border)]">
          <LanguageRegionPicker />
        </div>
        <div className="p-3">
          <HelpMenu />
        </div>
      </PopoverContent>
    </Popover>
  );
}

function Globe2Icon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.6">
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3a15 15 0 010 18M12 3a15 15 0 000 18" />
    </svg>
  );
}

function BellIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M6 8a6 6 0 0112 0v5l1.5 3h-15L6 13V8z" />
      <path d="M9 19a3 3 0 006 0" />
    </svg>
  );
}

function ProfileMenu({ user, onLogout, onSwitchShell }) {
  const navigate = useNavigate();
  const [open, setOpen] = useNavMenuOpen();
  const go = (to) => { setOpen(false); navigate(to); };
  return (
    <DropdownMenu open={open} onOpenChange={setOpen} modal={false}>
      <DropdownMenuTrigger asChild>
        <button className="h-9 w-9 rounded-full bg-[color:var(--st-surface-alt)] grid place-items-center text-[12px] font-medium text-[color:var(--st-text)]"
          aria-expanded={open}
          data-testid={NAV.profileMenu}>
          {(user?.name || user?.email || "U").slice(0, 1).toUpperCase()}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="text-xs">
          <div className="text-[color:var(--st-text)] font-medium">{user?.name || user?.email}</div>
          <div className="text-[10px] text-[color:var(--st-text-muted)] capitalize">{(user?.role || "").replace(/_/g, " ")}</div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => go("/my-devices")}><Laptop2 className="h-4 w-4 mr-2" /> {own(user, "devices")}</DropdownMenuItem>
        <DropdownMenuItem onSelect={() => go("/settings/experience")} data-testid="profile-experience"><Sparkles className="h-4 w-4 mr-2" /> Experience</DropdownMenuItem>
        <DropdownMenuItem onSelect={() => go("/users")}><UserCog className="h-4 w-4 mr-2" /> Users & Roles</DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => { setOpen(false); onSwitchShell(); }} data-testid="switch-to-classic">
          <Building2 className="h-4 w-4 mr-2" /> Switch to Classic navigation
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid={AUTH.logoutBtn} onSelect={() => { setOpen(false); onLogout(); }} className="text-rose-600 focus:text-rose-700">
          <LogOut className="h-4 w-4 mr-2" /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
